import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useMutation, gql } from '@apollo/client';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle, StyledDialogContent } from '../components/StyledComponents';

const GET_FUNCTIONS = gql`
  query GetFunctions {
    functions {
      id
      name
      description
      parentFunction {
        id
        name
      }
      department {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

const CREATE_FUNCTION = gql`
  mutation CreateFunction($name: String!, $description: String, $departmentId: ID, $parentFunctionId: ID) {
    createFunction(name: $name, description: $description, departmentId: $departmentId, parentFunctionId: $parentFunctionId) {
      success
      message
      function {
        id
        name
        description
        parentFunction {
          id
          name
        }
        department {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATE_FUNCTION = gql`
  mutation UpdateFunction($id: ID!, $name: String, $description: String, $departmentId: ID, $parentFunctionId: ID) {
    updateFunction(id: $id, name: $name, description: $description, departmentId: $departmentId, parentFunctionId: $parentFunctionId) {
      success
      message
      function {
        id
        name
        description
        parentFunction {
          id
          name
        }
        department {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  }
`;

const DELETE_FUNCTION = gql`
  mutation DeleteFunction($id: ID!) {
    deleteFunction(id: $id) {
      success
      message
    }
  }
`;

function Functions() {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    departmentId: '',
    parentFunctionId: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { loading, error: queryError, data, refetch } = useQuery(GET_FUNCTIONS);
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);

  const [createFunction] = useMutation(CREATE_FUNCTION, {
    onCompleted: (data) => {
      if (data.createFunction.success) {
        setSuccess(data.createFunction.message);
        setError('');
        handleCloseDialog();
        refetch();
      } else {
        setError(data.createFunction.message);
        setSuccess('');
      }
    },
    onError: (error) => {
      setError(error.message);
      setSuccess('');
    }
  });

  const [updateFunction] = useMutation(UPDATE_FUNCTION, {
    onCompleted: (data) => {
      if (data.updateFunction.success) {
        setSuccess(data.updateFunction.message);
        setError('');
        handleCloseDialog();
        refetch();
      } else {
        setError(data.updateFunction.message);
        setSuccess('');
      }
    },
    onError: (error) => {
      setError(error.message);
      setSuccess('');
    }
  });

  const [deleteFunction] = useMutation(DELETE_FUNCTION, {
    onCompleted: (data) => {
      if (data.deleteFunction.success) {
        setSuccess(data.deleteFunction.message);
        setError('');
        refetch();
      } else {
        setError(data.deleteFunction.message);
        setSuccess('');
      }
    },
    onError: (error) => {
      setError(error.message);
      setSuccess('');
    }
  });

  const handleOpenDialog = (functionData = null) => {
    if (functionData) {
      setEditingFunction(functionData);
        setFormData({
          name: functionData.name,
          description: functionData.description || '',
          departmentId: functionData.department?.id || '',
          parentFunctionId: functionData.parentFunction?.id || ''
        });
    } else {
      setEditingFunction(null);
      setFormData({ name: '', description: '', departmentId: '', parentFunctionId: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFunction(null);
    setFormData({ name: '', description: '', departmentId: '', parentFunctionId: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Le nom de la fonction est obligatoire');
      return;
    }

    try {
      const variables = {
        name: formData.name,
        description: formData.description,
        parentFunctionId: formData.parentFunctionId || null
      };

      // Ajouter le departmentId seulement s'il est sélectionné
      if (formData.departmentId) {
        variables.departmentId = formData.departmentId;
      }

      if (editingFunction) {
        await updateFunction({
          variables: {
            id: editingFunction.id,
            ...variables
          }
        });
      } else {
        await createFunction({
          variables
        });
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fonction ?')) {
      try {
        await deleteFunction({
          variables: { id }
        });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  // Filtrer les fonctions en fonction de la recherche
  const filteredFunctions = React.useMemo(() => {
    if (!data?.functions) return [];
    
    if (!searchQuery) return data.functions;
    
    const query = searchQuery.toLowerCase();
    return data.functions.filter(func => 
      func.name.toLowerCase().includes(query) ||
      (func.description && func.description.toLowerCase().includes(query)) ||
      (func.department?.name && func.department.name.toLowerCase().includes(query))
    );
  }, [data?.functions, searchQuery]);

  const hierarchyOptions = React.useMemo(() => {
    const allFunctions = data?.functions || [];
    return allFunctions.filter((func) => {
      if (editingFunction && String(func.id) === String(editingFunction.id)) {
        return false;
      }
      if (!formData.departmentId) {
        return true;
      }
      return String(func.department?.id || '') === String(formData.departmentId);
    });
  }, [data?.functions, editingFunction, formData.departmentId]);

  if (loading) return <Typography>Chargement...</Typography>;
  if (queryError) return <Typography color="error">Erreur: {queryError.message}</Typography>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Fonctions
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Rechercher"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            placeholder="Rechercher par nom, description ou département..."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Ajouter une fonction
          </Button>
        </Grid>
      </Grid>

      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Département</TableCell>
              <TableCell>Supérieur hiérarchique</TableCell>
              <TableCell>Date de création</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFunctions.map((function_, index) => (
              <TableRow 
                key={function_.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                  ...tableRowHoverStyles
                }}
              >
                <TableCell>{function_.name}</TableCell>
                <TableCell>{function_.description}</TableCell>
                <TableCell>{function_.department?.name || '-'}</TableCell>
                <TableCell>{function_.parentFunction?.name || '-'}</TableCell>
                <TableCell>
                  {new Date(function_.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(function_)}
                    sx={iconButtonStyles}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(function_.id)}
                    sx={iconButtonStyles}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <StyledDialogTitle>
          {editingFunction ? 'Modifier la fonction' : 'Ajouter une fonction'}
        </StyledDialogTitle>
        <form onSubmit={handleSubmit}>
          <StyledDialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nom"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Département</InputLabel>
              <Select
                value={formData.departmentId}
                onChange={(e) => setFormData({ ...formData, departmentId: e.target.value, parentFunctionId: '' })}
                label="Département"
              >
                <MenuItem value="">
                  <em>Aucun département</em>
                </MenuItem>
                {departmentsData?.departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Supérieur hiérarchique</InputLabel>
              <Select
                value={formData.parentFunctionId}
                onChange={(e) => setFormData({ ...formData, parentFunctionId: e.target.value })}
                label="Supérieur hiérarchique"
              >
                <MenuItem value="">
                  <em>Aucun supérieur</em>
                </MenuItem>
                {hierarchyOptions.map((functionOption) => (
                  <MenuItem key={functionOption.id} value={functionOption.id}>
                    {functionOption.name}{functionOption.department?.name ? ` - ${functionOption.department.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyledDialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingFunction ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Functions; 
